import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  MdTrendingUp,
  MdGroups,
  MdEventAvailable,
  MdExpandMore,
  MdExpandLess,
} from "react-icons/md";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import { format } from "date-fns";

/**
 * AdminKpisAndTrendsBare (YoY)
 * -------------------------------------------------
 * - KPI chips: This month (to-date) vs same month last year (to-date) + last month YoY in a note
 * - Trends: 12-month grouped bar chart with YoY overlays (this year + last year)
 * - No external charting library
 */
export default function AdminKpisAndTrendsBare({ school = null, middleContent = null }) {
  // KPI state (current vs last year to date)
  const [intro3, setIntro3] = useState([]); // [LY to-date, This to-date]
  const [hire3, setHire3] = useState([]);

  // Trends state (12 months, full)
  const [intro12, setIntro12] = useState([]);     // this year
  const [hire12, setHire12] = useState([]);       // this year
  const [intro12Ly, setIntro12Ly] = useState([]); // last year
  const [hire12Ly, setHire12Ly] = useState([]);   // last year

  const [expanded, setExpanded] = useState(false);
  const [loading3, setLoading3] = useState(true);
  const [loading12, setLoading12] = useState(false);
  const [error, setError] = useState(null);

  // For small “last month YoY” line
  const [introPrevYoY, setIntroPrevYoY] = useState({ curr: 0, ly: 0, deltaPct: 0 });
  const [hirePrevYoY, setHirePrevYoY]   = useState({ curr: 0, ly: 0, deltaPct: 0 });

  

  // ---- date windows for trends ----
  const last12 = useMemo(() => getBackMonths(12), []);
  const last12Ly = useMemo(
    () => last12.map(({ year, month }) => ({ year: year - 1, month })),
    [last12]
  );

  // --- KPI: YoY (this month to-date vs LY to-date) + (last month full vs LY full)
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading3(true);
      setError(null);
      try {
        const now = new Date();
        const y  = now.getUTCFullYear();
        const m  = now.getUTCMonth() + 1; // 1..12
        const d  = getUtcTodayDay();

        const { year: py, month: pm } = prevMonthOf(y, m);
        const { year: ly, month: lm } = sameMonthLastYearOf(y, m);
        const { year: lyPrev, month: lmPrev } = sameMonthLastYearOf(py, pm);

        // Intro — this month (to date) vs LY (to date)
        const [introNowTD, introLyTD] = await Promise.all([
          fetchMonthToDay(baseUrl, "/metrics/intro-lessons/month", y,  m,  d,  school),
          fetchMonthToDay(baseUrl, "/metrics/intro-lessons/month", ly, lm, d,  school),
        ]);

        // Intro — last month (full) vs LY last month (full)
        const [introPrevFull, introPrevLyFull] = await Promise.all([
          fetchMonthFull(baseUrl, "/metrics/intro-lessons/month", py, pm, school),
          fetchMonthFull(baseUrl, "/metrics/intro-lessons/month", lyPrev, lmPrev, school),
        ]);

        // Hire — this month (to date) vs LY (to date)
        const [hireNowTD, hireLyTD] = await Promise.all([
          fetchMonthToDay(baseUrl, "/metrics/teachers/hires/month", y,  m,  d,  school).catch(() => ({ count: 0, year: y, month: m })),
          fetchMonthToDay(baseUrl, "/metrics/teachers/hires/month", ly, lm, d,  school).catch(() => ({ count: 0, year: ly, month: lm })),
        ]);

        // Hire — last month (full) vs LY last month (full)
        const [hirePrevFull, hirePrevLyFull] = await Promise.all([
          fetchMonthFull(baseUrl, "/metrics/teachers/hires/month", py, pm, school).catch(() => ({ count: 0 })),
          fetchMonthFull(baseUrl, "/metrics/teachers/hires/month", lyPrev, lmPrev, school).catch(() => ({ count: 0 })),
        ]);

        if (!mounted) return;

        setIntro3([
          { year: introLyTD.year, month: introLyTD.month, count: introLyTD.count }, // LY (to-date)
          { year: introNowTD.year, month: introNowTD.month, count: introNowTD.count }, // This (to-date)
        ]);
        setHire3([
          { year: hireLyTD.year, month: hireLyTD.month, count: hireLyTD.count },
          { year: hireNowTD.year, month: hireNowTD.month, count: hireNowTD.count },
        ]);

        setIntroPrevYoY({
          curr: introPrevFull.count,
          ly:   introPrevLyFull.count,
          deltaPct: yoyPct(introPrevFull.count, introPrevLyFull.count),
        });
        setHirePrevYoY({
          curr: hirePrevFull.count,
          ly:   hirePrevLyFull.count,
          deltaPct: yoyPct(hirePrevFull.count, hirePrevLyFull.count),
        });
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load KPIs");
      } finally {
        if (mounted) setLoading3(false);
      }
    })();
    return () => { mounted = false; };
  }, [school]);

  const kpiIntro = computeKpiYoY(intro3); // this month (to-date) vs last year same month (to-date)
  const kpiHire  = computeKpiYoY(hire3);
// Add this near your other refs/state (top-level in the component):
const fetchedKeyRef = useRef(""); // prevents duplicate fetches across renders

useEffect(() => {
  if (!expanded) return;

  // Build a stable key for the current 12-month window + school
  const first = last12[0];
  const last  = last12[last12.length - 1];
  const key = `${school || "ALL"}:${first.year}-${first.month}->${last.year}-${last.month}`;

  // Skip if we already fetched this exact window
  if (fetchedKeyRef.current === key) return;
  fetchedKeyRef.current = key;

  let mounted = true;
  setLoading12(true);

  (async () => {
    try {
      const [
        introTY, hireTY,     // this year
        introLY, hireLY      // last year (aligned by month)
      ] = await Promise.all([
        fetchManyMonths(baseUrl, "/metrics/intro-lessons/month", last12,   school),
        fetchManyMonths(baseUrl, "/metrics/teachers/hires/month", last12, school).catch(() => []),
        fetchManyMonths(baseUrl, "/metrics/intro-lessons/month", last12Ly, school),
        fetchManyMonths(baseUrl, "/metrics/teachers/hires/month", last12Ly, school).catch(() => []),
      ]);

      if (!mounted) return;
      setIntro12(introTY);
      setHire12(hireTY);
      setIntro12Ly(introLY);
      setHire12Ly(hireLY);
    } catch (e) {
      if (!mounted) return;
      setError(e?.message || "Failed to load trends");
      // If it failed, clear the key so a future open can retry
      fetchedKeyRef.current = "";
    } finally {
      if (mounted) setLoading12(false);
    }
  })();

  return () => { mounted = false; };
  // IMPORTANT: no intro12.length in deps; key guards refetching
}, [expanded, school, last12, last12Ly, baseUrl]);


  // Prepare rows with YoY
  const rows = useMemo(
    () => mergeByKeyYoY(last12, intro12, hire12, intro12Ly, hire12Ly),
    [last12, intro12, hire12, intro12Ly, hire12Ly]
  );

  const { monthName: prevName }   = getPrevMonthInfoUTC();
const { monthName: prevLyName } = getPrevMonthLastYearInfoUTC();


  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        {/* <div>
          <p className="text-sm text-gray-500">
            YoY view · {school ? `School: ${school}` : "All schools"}
          </p>
        </div> */}
      </div>

{/* KPI Cards (YoY) */}
<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
  <KpiCard
    title="Intro Lessons"
    icon={<MdEventAvailable className="h-4 w-4" />}
    loading={loading3}
    valueNow={kpiIntro.thisMonth}              // this month (to-date)
    valuePrev={kpiIntro.lastYearSameMonth}     // same month last year (to-date)
    deltaPct={kpiIntro.deltaPct}
    secondary={`${prevName}: ${introPrevYoY.curr} | ${prevLyName} Last Year: ${introPrevYoY.ly}`}
  />
  <KpiCard
    title="Teachers Hired"
    icon={<MdGroups className="h-4 w-4" />}
    loading={loading3}
    valueNow={kpiHire.thisMonth}
    valuePrev={kpiHire.lastYearSameMonth}
    deltaPct={kpiHire.deltaPct}
    secondary={`${prevName}: ${hirePrevYoY.curr} | ${prevLyName} Last Year: ${hirePrevYoY.ly}`}
  />
</div>

      {middleContent}

      {/* Expand / Collapse Trends */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-medium">12-Month Trends (Year over year)</h3>
            <p className="text-xs text-gray-500">Light bars = last year, Bold bars = this year</p>
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 rounded-xl border px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            {expanded ? (<><MdExpandLess className="h-4 w-4" /><span>Hide</span></>) : (<><MdExpandMore className="h-4 w-4" /><span>See trends</span></>)}
          </button>
        </div>

        {expanded && (
          <div className="mt-4">
            {loading12 ? (
              <div className="text-sm text-gray-500">Loading trends…</div>
            ) : error ? (
              <div className="text-sm text-red-500">{String(error)}</div>
            ) : (
              <SimpleBarChartYoY rows={rows} height={320} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* -----------------------
 * KPI Card
 * ---------------------*/
function KpiCard({
  title, icon, loading,
  valueNow = 0, valuePrev = 0, deltaPct = 0,
  rightLabel, // optional; if omitted we auto-set "<Month> last year (to-date) <emoji>"
  note, secondary // optional; if omitted we auto-set "<Prev Month> vs <Prev Month last year>" with emojis
}) {
  const up = deltaPct >= 0;

  // Month info (UTC to align with server windows)
  const { monthIndex: mIdxNow, monthName: monthNow } = getCurrentMonthInfoUTC();
  const { monthIndex: mIdxPrev, monthName: monthPrev } = getPrevMonthInfoUTC();
  const { monthName: monthNowLY, monthIndex: mIdxNowLY } = getSameMonthLastYearInfoUTC();
  const { monthName: monthPrevLY, monthIndex: mIdxPrevLY } = getPrevMonthLastYearInfoUTC();

  const monthEmojiNow    = emojiForMonth(mIdxNow);
  const monthEmojiPrev   = emojiForMonth(mIdxPrev);
  const monthEmojiNowLY  = emojiForMonth(mIdxNowLY);
  const monthEmojiPrevLY = emojiForMonth(mIdxPrevLY);

  const rightText = rightLabel || `${monthNow} Last Year (by ${format(new Date(), 'MMM d')})`;
  const secondaryAuto = `Last month: ${monthPrev} ${monthEmojiPrev} | ${monthPrevLY} last year ${monthEmojiPrevLY}`;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-gray-50">
            {icon}
          </div>
          <div className="text-sm font-medium">{title}</div>
        </div>
        <div className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
          <MdTrendingUp className={`h-3.5 w-3.5 ${up ? "" : "rotate-180"}`} />
          <span>{loading ? "…" : `${up ? "+" : ""}${formatPct(deltaPct)}`}</span>
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between">
        <div>
          <div className="text-3xl font-semibold tabular-nums">{loading ? "…" : valueNow}</div>
          <div className="text-xs text-gray-500">
            {monthNow} {monthEmojiNow} (to date)
          </div>
        </div>
        <div className="text-right">
          <div className="text-base font-semibold tabular-nums text-gray-700">{loading ? "…" : valuePrev}</div>
          <div className="text-xs text-gray-500">{rightText}</div>
        </div>
      </div>

      {/* Default secondary shows "last month" names with emojis if not provided */}
      <div className="mt-2 text-[11px] text-gray-500">
        {secondary || secondaryAuto}
      </div>

      {note && <div className="mt-1 text-[11px] text-gray-400">{note}</div>}
    </div>
  );
}

/* ---- Tiny helpers (UTC to match backend windows) ---- */

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

function getCurrentMonthInfoUTC() {
  const now = new Date();
  const monthIndex = now.getUTCMonth(); // 0..11
  return { monthIndex, monthName: MONTH_NAMES[monthIndex] };
}

function getPrevMonthInfoUTC() {
  const now = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1));
  now.setUTCMonth(now.getUTCMonth() - 1);
  const monthIndex = now.getUTCMonth();
  return { monthIndex, monthName: MONTH_NAMES[monthIndex] };
}

function getSameMonthLastYearInfoUTC() {
  const now = new Date(Date.UTC(new Date().getUTCFullYear() - 1, new Date().getUTCMonth(), 1));
  const monthIndex = now.getUTCMonth();
  return { monthIndex, monthName: MONTH_NAMES[monthIndex] };
}

function getPrevMonthLastYearInfoUTC() {
  const now = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1));
  now.setUTCMonth(now.getUTCMonth() - 1); // previous month this year
  now.setUTCFullYear(now.getUTCFullYear() - 1); // move to last year, same (prev) month
  const monthIndex = now.getUTCMonth();
  return { monthIndex, monthName: MONTH_NAMES[monthIndex] };
}

function emojiForMonth(m) {
  switch (m) {
    case 0:  return "❄️";   // Jan
    case 1:  return "💘";   // Feb
    case 2:  return "🍀";   // Mar
    case 3:  return "🌸";   // Apr
    case 4:  return "🌼";   // May
    case 5:  return "☀️";   // Jun
    case 6:  return "🎆";   // Jul
    case 7:  return "🏖️";  // Aug
    case 8:  return "🍎";   // Sep
    case 9:  return "🎃";   // Oct
    case 10: return "🦃";   // Nov
    case 11: return "🎄";   // Dec
    default: return "📅";
  }
}


/* -----------------------
 * YoY Grouped bar chart (SVG only, responsive) + tooltip + legend
 * ---------------------*/
function SimpleBarChartYoY({ rows, height = 320 }) {
  const wrapperRef = useRef(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const [tip, setTip] = useState({ show: false, x: 0, y: 0, i: -1 });

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const readWidth = () => {
      const w = el.clientWidth || el.getBoundingClientRect().width || 0;
      setMeasuredWidth(w);
    };
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(readWidth);
      ro.observe(el);
      readWidth();
      return () => ro.disconnect();
    } else {
      window.addEventListener("resize", readWidth);
      readWidth();
      return () => window.removeEventListener("resize", readWidth);
    }
  }, []);

  const safeWidth = Math.max(320, Math.floor(measuredWidth || 0));
  const svgW = safeWidth;
  const svgH = height;

  // Layout
  const margin = { top: 10, right: 10, bottom: 32, left: 32 };
  const innerW = Math.max(0, svgW - margin.left - margin.right);
  const innerH = Math.max(0, svgH - margin.top - margin.bottom);

  // Scales
  const maxY = Math.max(
    1,
    ...rows.map(r => Math.max(r.intro, r.hire, r.introLy, r.hireLy))
  );
  const yTop = (v) => innerH - (v / maxY) * innerH;

  const n = rows.length;
  const gap = 18;     // space between month groups
  const barGap = 4;   // space between bars inside a group
  // 4 bars per group (Intro LY, Intro, Hire LY, Hire)
  const groupW = n ? (innerW - gap * Math.max(0, n - 1)) / n : 0;
  const barW = Math.max(3, (groupW - barGap * 3) / 4);

  const groupX = (i) => margin.left + i * (groupW + gap);
  const clientToSvgX = (evt, svg) => {
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM()?.inverse();
    return ctm ? pt.matrixTransform(ctm).x : 0;
  };

  const handleEnterGroup = (i, x, y) => setTip({ show: true, x, y, i });
  const handleLeave = () => setTip({ show: false, x: 0, y: 0, i: -1 });
  const handleMouseMove = (e) => {
    const svg = e.currentTarget;
    const xSvg = clientToSvgX(e, svg);
    const xRel = Math.min(Math.max(xSvg - margin.left, 0), innerW);
    const span = groupW + gap;
    let i = Math.floor(xRel / span);
    i = Math.max(0, Math.min(n - 1, i));
    setTip({ show: true, x: xSvg, y: margin.top, i });
  };

  const tipRow = tip.i >= 0 ? rows[tip.i] : null;

  return (
    <div ref={wrapperRef} className="relative w-full min-w-[320px]" style={{ height }}>
      {/* Legend */}
      <div className="absolute right-2 top-0 z-10 flex gap-3 text-[11px] text-gray-600 bg-white/80 backdrop-blur px-2 py-1 rounded-md border border-gray-200">
        <LegendSwatch color="#93C5FD" label="Intros Last Year" />
        <LegendSwatch color="#3B82F6" label="Intros" />
        <LegendSwatch color="#6EE7B7" label="Hired Last Year" />
        <LegendSwatch color="#10B981" label="Hired" />
      </div>

      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${svgW} ${svgH}`}
        preserveAspectRatio="none"
        className="overflow-visible"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleLeave}
      >
        {/* Y grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
          const y = margin.top + innerH * (1 - t);
          const val = Math.round(maxY * t);
          return (
            <g key={i}>
              <line x1={margin.left} x2={svgW - margin.right} y1={y} y2={y} stroke="rgba(0,0,0,0.06)" />
              <text x={margin.left - 6} y={y} textAnchor="end" dominantBaseline="middle" fill="#9CA3AF" style={{ fontSize: 10 }}>
                {val}
              </text>
            </g>
          );
        })}

        {/* Bars + X labels */}
        {rows.map((r, idx) => {
          const gx = groupX(idx);
          // X positions for 4 bars within the group
          const xIntroLy = gx;
          const xIntro    = gx + (barW + barGap);
          const xHireLy   = gx + 2 * (barW + barGap);
          const xHire     = gx + 3 * (barW + barGap);

          // Heights
          const hIntroLy = innerH - yTop(r.introLy);
          const hIntro   = innerH - yTop(r.intro);
          const hHireLy  = innerH - yTop(r.hireLy);
          const hHire    = innerH - yTop(r.hire);

          return (
            <g key={r.key}>
              {/* Bars */}
              <rect x={xIntroLy} y={margin.top + yTop(r.introLy)} width={barW} height={hIntroLy} rx={5} fill="#93C5FD" fillOpacity="0.9" />
              <rect x={xIntro}   y={margin.top + yTop(r.intro)}   width={barW} height={hIntro}   rx={5} fill="#3B82F6" fillOpacity="0.9" />
              <rect x={xHireLy}  y={margin.top + yTop(r.hireLy)}  width={barW} height={hHireLy}  rx={5} fill="#6EE7B7" fillOpacity="0.9" />
              <rect x={xHire}    y={margin.top + yTop(r.hire)}    width={barW} height={hHire}    rx={5} fill="#10B981" fillOpacity="0.9" />

              {/* X label */}
              <text x={gx + groupW / 2} y={svgH - 6} textAnchor="middle" fill="#6B7280" style={{ fontSize: 10 }}>
                {r.label}
              </text>

              {/* Invisible hotspot */}
              <rect
                x={gx - gap / 2}
                y={margin.top}
                width={groupW + gap}
                height={innerH}
                fill="transparent"
                tabIndex={0}
                aria-label={`${r.label}: Intro ${r.intro} (LY ${r.introLy}), Hired ${r.hire} (LY ${r.hireLy})`}
                onFocus={() => handleEnterGroup(idx, gx + groupW / 2, margin.top)}
                onBlur={handleLeave}
                onMouseEnter={() => handleEnterGroup(idx, gx + groupW / 2, margin.top)}
              />
            </g>
          );
        })}

        {/* Hover guide line */}
        {tip.show && tipRow && (
          <line
            x1={groupX(tip.i) + groupW / 2}
            x2={groupX(tip.i) + groupW / 2}
            y1={margin.top}
            y2={margin.top + innerH}
            stroke="rgba(0,0,0,0.12)"
          />
        )}
      </svg>

      {/* Tooltip */}
      {tip.show && tipRow && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs shadow-md"
          style={{
            left: `${Math.min(Math.max(tip.x, 40), svgW - 40)}px`,
            top:  `12px`,
          }}
        >
          <div className="mb-1 font-medium text-gray-700">{tipRow.label}</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <TipRow color="#3B82F6" label="Intro"   value={tipRow.intro} />
            <TipRow color="#10B981" label="Hired"   value={tipRow.hire} />
            <TipRow color="#93C5FD" label="Intro (LY)" value={tipRow.introLy} />
            <TipRow color="#6EE7B7" label="Hired (LY)" value={tipRow.hireLy} />
          </div>
        </div>
      )}
    </div>
  );
}

function LegendSwatch({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </span>
  );
}
function TipRow({ color, label, value }) {
  return (
    <div className="flex items-center gap-1">
      <span className="inline-block h-2 w-2 rounded-sm" style={{ backgroundColor: color }} />
      <span className="text-gray-500">{label}:</span>
      <span className="ml-1 font-semibold text-gray-800 tabular-nums">{value}</span>
    </div>
  );
}

/* -----------------------
 * Helpers
 * ---------------------*/

// Dates
function getUtcTodayDay() {
  const now = new Date();
  return now.getUTCDate(); // 1..31
}
function prevMonthOf(year, month1to12) {
  const d = new Date(Date.UTC(year, month1to12 - 1, 1));
  d.setUTCMonth(d.getUTCMonth() - 1);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
}
function sameMonthLastYearOf(year, month1to12) {
  return { year: year - 1, month: month1to12 };
}

// API helpers
async function fetchMonthToDay(baseUrl, path, year, month, uptoDay, school) {
  const res = await axios.get(`${baseUrl}${path}`, {
    ...getRequestHeaders(),
    params: { year, month, uptoDay, ...(school ? { school } : {}) },
  });
  return { year: res.data.year, month: res.data.month, count: res.data.count };
}
async function fetchMonthFull(baseUrl, path, year, month, school) {
  const res = await axios.get(`${baseUrl}${path}`, {
    ...getRequestHeaders(),
    params: { year, month, ...(school ? { school } : {}) },
  });
  return { year: res.data.year, month: res.data.month, count: res.data.count };
}

// Trends fetch
async function fetchManyMonths(baseUrl, path, months, school) {
  const CONCURRENCY = 4;
  const TIMEOUT_MS = 12000;
  const queue = [...months];
  const out = [];

  async function runOne({ year, month }) {
    try {
      const res = await axios.get(`${baseUrl}${path}`, {
        ...getRequestHeaders(),
        timeout: TIMEOUT_MS,
        params: { year, month, ...(school ? { school } : {}) },
      });
      return { year: res.data.year, month: res.data.month, count: res.data.count };
    } catch {
      return { year, month, count: 0 };
    }
  }

  const workers = Array.from({ length: CONCURRENCY }, async () => {
    while (queue.length) {
      const next = queue.shift();
      const result = await runOne(next);
      out.push(result);
    }
  });
  await Promise.all(workers);

  out.sort((a, b) => a.year - b.year || a.month - b.month);
  return out;
}

// Transforms
function getBackMonths(n) {
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: formatMonthShort(d),
    });
  }
  return out;
}

// KPI compute (YoY)
function computeKpiYoY(arr /* [LY, This] */) {
  const len = arr.length;
  const thisMonth = len ? arr[len - 1].count : 0;
  const lastYearSameMonth = len > 1 ? arr[len - 2].count : 0;
  const deltaPct = yoyPct(thisMonth, lastYearSameMonth);
  return { thisMonth, lastYearSameMonth, deltaPct };
}
function yoyPct(curr, lastYear) {
  return lastYear === 0 ? (curr > 0 ? 1 : 0) : (curr - lastYear) / lastYear;
}

// Merge for YoY chart rows
function mergeByKeyYoY(monthsWindow, introTY, hireTY, introLY, hireLY) {
  const m = new Map();
  monthsWindow.forEach((mw) =>
    m.set(mw.key, { key: mw.key, label: mw.label, intro: 0, hire: 0, introLy: 0, hireLy: 0 })
  );
  introTY.forEach((x) => {
    const key = `${x.year}-${String(x.month).padStart(2, "0")}`;
    if (m.has(key)) m.get(key).intro = x.count;
  });
  hireTY.forEach((x) => {
    const key = `${x.year}-${String(x.month).padStart(2, "0")}`;
    if (m.has(key)) m.get(key).hire = x.count;
  });
  introLY.forEach((x) => {
    // Map last-year datapoint to the current window’s same month label
    const key = `${x.year + 1}-${String(x.month).padStart(2, "0")}`; // shift forward a year to align under current labels
    if (m.has(key)) m.get(key).introLy = x.count;
  });
  hireLY.forEach((x) => {
    const key = `${x.year + 1}-${String(x.month).padStart(2, "0")}`;
    if (m.has(key)) m.get(key).hireLy = x.count;
  });
  return Array.from(m.values());
}

function formatMonthShort(d) {
  const m = d.toLocaleString("en-US", { month: "short" });
  const y = String(d.getFullYear()).slice(2);
  return `${m} '${y}`;
}
function formatPct(x) {
  return `${(x * 100).toFixed(0)}%`;
}
